import { useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import { useCartItems } from "@/components/cart/hooks/useCartItems";
import { GetMenuItemForCartDocument } from "@/graphql/cart/cart.hooks";
import { normalizeCartDishItemAgainstDetails } from "@/components/cart/helpers/normalizeCartDishItemAgainstDetails";
import {
  getCartDish,
  removeCartDish,
  upsertCartDish,
} from "@/components/cart/services/cartService";
import type { ICartDetailsForNormalize } from "@/types";
import type { IMenuItemForCart } from "@/components/cart/helpers/mapCartDishToCardVm";
import { getMenuTypeFromUrl } from "@/components/cart/helpers/cartEvents";

export type TMenuItemForCartDetails = IMenuItemForCart &
  ICartDetailsForNormalize & {
    deleted?: boolean | null;
  };

type TDetailsMap = Map<number, TMenuItemForCartDetails>;

type TLoadResult = {
  dishId: number;
  menuItem: TMenuItemForCartDetails | null;
  hasAnyError: boolean;
};

export const useCartDetailsMap = (
  menuIdNumber: number | null,
  enabled: boolean
) => {
  const apolloClient = useApolloClient();
  const { locale, branchId, typeMenu } = useParams();
  const menuTypeEnum = useMemo(() => getMenuTypeFromUrl(typeMenu), [typeMenu]);
  const cartItems = useCartItems(menuIdNumber);

  const [detailsMap, setDetailsMap] = useState<TDetailsMap>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const dishIdsKey = useMemo(() => {
    if (!menuIdNumber || !locale) return "";
    return `${locale}|${cartItems
      .map((item) => String(item.dishId))
      .sort()
      .join("|")}`;
  }, [cartItems, menuIdNumber, locale]);

  const lastDishIdsKeyRef = useRef("");

  useEffect(() => {
    if (!enabled) lastDishIdsKeyRef.current = "";
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (!menuIdNumber) return;
    if (!locale) return;

    if (lastDishIdsKeyRef.current === dishIdsKey) return;
    lastDishIdsKeyRef.current = dishIdsKey;

    let isCancelled = false;

    const loadOne = async (dishId: number): Promise<TLoadResult> => {
      try {
        const result = await apolloClient.query({
          query: GetMenuItemForCartDocument,
          variables: {
            menuId: menuIdNumber,
            dishId,
            locale,
            branchId: Number(branchId),
            typeMenu: menuTypeEnum,
          },
          fetchPolicy: "network-only",
          errorPolicy: "all",
          context: {
            headers: {
              "X-LOCALE": locale,
            },
          },
        });

        const menuItem = (result.data?.menu?.items?.[0] ??
          null) as TMenuItemForCartDetails | null;

        const hasGraphQLErrors = Boolean(result.error?.graphQLErrors?.length);
        const hasNetworkError = Boolean(result.error?.networkError);
        const hasAnyError = hasGraphQLErrors || hasNetworkError;

        return { dishId, menuItem, hasAnyError };
      } catch {
        return { dishId, menuItem: null, hasAnyError: true };
      }
    };

    const load = async () => {
      setIsLoading(true);

      try {
        const dishIdsSet = new Set(cartItems.map((item) => item.dishId));

        const results = await Promise.all(
          cartItems.map((item) => loadOne(item.dishId))
        );

        if (isCancelled) return;

        // update detailsMap
        // remove items that are no longer in the cart
        setDetailsMap((previousMap) => {
          const nextMap = new Map<number, TMenuItemForCartDetails>();

          for (const [dishId, previousDetails] of previousMap) {
            if (dishIdsSet.has(dishId)) nextMap.set(dishId, previousDetails);
          }

          for (const result of results) {
            if (result.hasAnyError) continue;

            const menuItem = result.menuItem;
            if (!menuItem || menuItem.deleted) {
              nextMap.delete(result.dishId);
              continue;
            }

            nextMap.set(result.dishId, menuItem);
          }

          return nextMap;
        });

        for (const result of results) {
          if (result.hasAnyError) continue;

          const menuItem = result.menuItem;

          if (!menuItem || menuItem.deleted) {
            removeCartDish(menuIdNumber, result.dishId);
            continue;
          }

          const detailsForNormalize = {
            portions: menuItem.portions ?? [],
            supplements: (menuItem.supplements ?? [])
              .filter((group) => !group.deleted)
              .map((group) => ({
                id: group.id,
                options: (group.options ?? [])
                  .filter((option) => !option.deleted)
                  .map((option) => ({ id: option.id })),
              })),
          };

          const saved = getCartDish(menuIdNumber, result.dishId);
          if (!saved) continue;

          const { normalized, changed } = normalizeCartDishItemAgainstDetails(
            saved,
            detailsForNormalize
          );

          if (changed) upsertCartDish(menuIdNumber, normalized);
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      isCancelled = true;
    };
  }, [
    apolloClient,
    cartItems,
    dishIdsKey,
    enabled,
    menuIdNumber,
    locale,
    branchId,
    menuTypeEnum,
  ]);

  return { detailsMap, isLoading, cartItems };
};
